import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import BodyText from 'LuxKit/Typography/BodyText'
import { Fragment } from 'react'
import { useIntl } from 'react-intl'

interface Props {
  currentBalance: number
  offer: App.LoyaltyPurchasePointsOffer
}

function LoyaltyPurchasePointsBreakdown(props: Props) {
  const { currentBalance, offer } = props
  const intl = useIntl()
  const formatCurrency = useCurrencyFormatter()

  const ptsLabel = intl.formatMessage({
    id: 'YbCPAk5imc72ZPZPmXbj',
    defaultMessage: 'pts',
    description: 'Short label for loyalty points used in breakdown rows',
  })

  const balanceAfter = currentBalance + offer.totalPoints

  const rows: Array<{ key: string, label: string, value: React.ReactNode, valueColour?: 'success', boldLabel?: boolean }> = [
    {
      key: 'currentBalance',
      label: intl.formatMessage({
        id: 'UsVSJuWB5HzZ6KlsUJ3x',
        defaultMessage: 'Current balance',
        description: 'Breakdown row label for the member’s existing points balance',
      }),
      value: <><FormatDecimal value={currentBalance} /> {ptsLabel}</>,
    },
    {
      key: 'pointsToPurchase',
      label: intl.formatMessage({
        id: 'c80soyTNj3ycQ4Jxasrf',
        defaultMessage: 'Points to purchase',
        description: 'Breakdown row label for the base points the member is buying',
      }),
      value: <><FormatDecimal value={offer.basePoints} /> {ptsLabel}</>,
    },
    ...(offer.bonusPoints > 0 ? [{
      key: 'bonus',
      label: intl.formatMessage({
        id: 'cihrkcgVIu94cBKrChfB',
        defaultMessage: 'Bonus points',
        description: 'Buy-points landing page breakdown row label for campaign bonus points',
      }),
      value: <><FormatDecimal value={offer.bonusPoints} /> {ptsLabel}</>,
      valueColour: 'success' as const,
    }] : []),
    {
      key: 'total',
      label: intl.formatMessage({
        id: 'Evrc5ZnM8kA9WEMa4sR7',
        defaultMessage: 'Total',
        description: 'Breakdown row label for the total price the member pays for the offer',
      }),
      value: formatCurrency(offer.publicPrice),
    },
    {
      key: 'balanceAfter',
      label: intl.formatMessage({
        id: 'CRtm7HmqEed6jJYCjhsk',
        defaultMessage: 'Balance after purchase',
        description: 'Breakdown row label for the member’s balance after the purchase clears',
      }),
      value: <><FormatDecimal value={balanceAfter} /> {ptsLabel}</>,
      boldLabel: true,
    },
  ]

  return <Group direction="vertical" gap={4}>
    {rows.map(row => {
      const isBonus = row.valueColour === 'success'
      const labelWeight = isBonus || row.boldLabel ? 'bold' : 'normal'
      const colour = isBonus ? 'success' : undefined
      const renderRow = (variant: 'medium' | 'large') => (
        <Group direction="horizontal" horizontalAlign="space-between" gap={12}>
          <BodyText variant={variant} weight={labelWeight} colour={colour}>{row.label}</BodyText>
          <BodyText variant={variant} weight="bold" colour={colour}>{row.value}</BodyText>
        </Group>
      )

      if (!isBonus && row.boldLabel) {
        return <Fragment key={row.key}>
          <CSSBreakpoint max="mobile" dynamic>{renderRow('medium')}</CSSBreakpoint>
          <CSSBreakpoint min="tablet" dynamic>{renderRow('large')}</CSSBreakpoint>
        </Fragment>
      }

      return <Fragment key={row.key}>{renderRow('medium')}</Fragment>
    })}
  </Group>
}

export default LoyaltyPurchasePointsBreakdown
